import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../app/providers';
import { href, Link, navigate } from '../../app/router';
import { ApiError } from '../../lib/api';
import { buttonClass } from '../../ui';
import { AuthLayout } from '../auth/AuthLayout';
import { useSession } from '../auth/session';
import { GuestEntry } from './GuestEntry';
import { pendingToken, takeStashedToken } from './token';

type Step = 'idle' | 'entering' | 'redeeming' | 'account' | 'expired' | 'invalid' | 'error';

/**
 * /s#<token> (spec §9.3): the token leaves the address bar before any request. Signed in (member, external or
 * returning guest) → redeemed at once; signed out → name + Turnstile, anonymous sign-in, then redeemed.
 */
export function SharePage() {
  const { t } = useTranslation();
  const api = useApi();
  const session = useSession();
  const [token] = useState(() => pendingToken('share'));
  const [step, setStep] = useState<Step>('idle');

  const redeem = useCallback(async () => {
    if (!token) return;
    setStep('redeeming');
    try {
      const { boardId } = await api.redeemShareLink(token);
      takeStashedToken('share');
      // A link to one page (P14) opens on it.
      const page = new URLSearchParams(window.location.search).get('page');
      navigate(page ? `${href('board', { boardId })}?page=${encodeURIComponent(page)}` : href('board', { boardId }), { replace: true });
    } catch (err) {
      const code = err instanceof ApiError ? err.code : null;
      if (code !== 'account_required') takeStashedToken('share');
      setStep(code === 'account_required' ? 'account' : code === 'expired' ? 'expired' : code === 'not_found' ? 'invalid' : 'error');
    }
  }, [api, token]);

  useEffect(() => {
    if (token && step === 'idle' && session.status === 'signed-in') void redeem();
  }, [token, step, session.status, redeem]);

  if (!token || step === 'invalid') return <Message title={t('guest.shareTitle')} body={t('guest.invalid')} />;
  if (step === 'expired') return <Message title={t('guest.shareTitle')} body={t('guest.expired')} />;
  if (step === 'error') return <Message title={t('guest.shareTitle')} body={t('guest.error')} />;
  if (step === 'account') {
    const next = { next: href('share') };
    return (
      <AuthLayout title={t('guest.accountTitle')}>
        <p className="font-sans text-fg-muted">{t('guest.accountBody')}</p>
        <div className="flex flex-wrap gap-3">
          <Link className={buttonClass('primary')} to={href('login', {}, next)}>
            {t('guest.signIn')}
          </Link>
        </div>
      </AuthLayout>
    );
  }
  if (session.status === 'signed-out' || step === 'entering') {
    return (
      <AuthLayout title={t('guest.shareTitle')} hero={t('guest.heroTitle')}>
        <GuestEntry
          onSubmit={async (name, captchaToken) => {
            setStep('entering');
            try {
              await api.auth.signInAnonymously(captchaToken);
              await api.updateProfile({ displayName: name });
            } catch (err) {
              setStep('idle');
              throw err;
            }
            await redeem();
          }}
        />
      </AuthLayout>
    );
  }
  return (
    <p role="status" className="flex h-dvh items-center justify-center font-sans text-fg-muted">
      {t('guest.opening')}
    </p>
  );
}

export function Message({ title, body, children }: { title: string; body: string; children?: React.ReactNode }) {
  return (
    <AuthLayout title={title}>
      <p role="alert" className="font-sans text-fg">
        {body}
      </p>
      {children}
    </AuthLayout>
  );
}
